import React, { useState, useEffect } from 'react';
import { format, formatDistanceToNow, isBefore, parseISO } from 'date-fns';
import { Clock, ChevronLeft, ChevronRight, CheckCircle2, XCircle, Check, X } from 'lucide-react';

interface ScheduleTimelineProps {
  booking: {
    drop_off_date?: string;
    drop_off_time?: string;
    start_date?: string;
    start_time?: string;
    end_date?: string;
    end_time?: string;
    mobile_booking?: boolean;
    status: string;
  };
  isEditing?: boolean;
  onEdit?: () => void;
  canEdit?: boolean;
  isMobile?: boolean;
  onStatusChange?: (status: string) => void;
  readOnly?: boolean;
  onRescheduleMobileBooking?: () => void;
  onUpdateSchedule?: (data: {
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
  }) => Promise<void>;
}

interface TimePoint {
  type: 'drop-off' | 'start' | 'end';
  date?: string;
  time?: string;
  label: string;
  isComplete?: boolean;
  isCurrent?: boolean;
}

export default function ScheduleTimeline({
  booking,
  isEditing = false,
  onEdit,
  canEdit = false,
  isMobile = false,
  onStatusChange,
  readOnly = false,
  onRescheduleMobileBooking,
  onUpdateSchedule
}: ScheduleTimelineProps) {
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);
  const [editedStartDate, setEditedStartDate] = useState('');
  const [editedStartTime, setEditedStartTime] = useState('');
  const [editedEndDate, setEditedEndDate] = useState('');
  const [editedEndTime, setEditedEndTime] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [originalDuration, setOriginalDuration] = useState(0); // Duration in minutes
  const [manuallyEditedEnd, setManuallyEditedEnd] = useState(false); // Track if user manually edited end

  // Initialize edit state when entering edit mode
  useEffect(() => {
    if (isEditingSchedule) {
      setEditedStartDate(booking.start_date || '');
      setEditedStartTime(booking.start_time || '');
      setEditedEndDate(booking.end_date || '');
      setEditedEndTime(booking.end_time || '');
      setManuallyEditedEnd(false);
      
      // Calculate original duration in minutes
      if (booking.start_date && booking.start_time && booking.end_date && booking.end_time) {
        const start = new Date(`${booking.start_date}T${booking.start_time}`);
        const end = new Date(`${booking.end_date}T${booking.end_time}`);
        const durationMinutes = Math.round((end.getTime() - start.getTime()) / (1000 * 60));
        setOriginalDuration(durationMinutes);
      }
    }
  }, [isEditingSchedule, booking]);

  // Auto-adjust end date/time when start changes (only if user hasn't manually edited end)
  useEffect(() => {
    if (isEditingSchedule && !manuallyEditedEnd && originalDuration > 0 && editedStartDate && editedStartTime) {
      try {
        const newStart = new Date(`${editedStartDate}T${editedStartTime}`);
        const newEnd = new Date(newStart.getTime() + originalDuration * 60 * 1000);
        
        const newEndDate = format(newEnd, 'yyyy-MM-dd');
        const newEndTime = format(newEnd, 'HH:mm');
        
        setEditedEndDate(newEndDate);
        setEditedEndTime(newEndTime);
      } catch (error) {
        console.error('Error calculating new end time:', error);
      }
    }
  }, [editedStartDate, editedStartTime, isEditingSchedule, manuallyEditedEnd, originalDuration]);

  const handleEndDateChange = (value: string) => {
    setEditedEndDate(value);
    setManuallyEditedEnd(true); // User manually edited, stop auto-adjusting
  };

  const handleEndTimeChange = (value: string) => {
    setEditedEndTime(value);
    setManuallyEditedEnd(true); // User manually edited, stop auto-adjusting
  };

  // Validate that end is at least 30 minutes after start
  const scheduleValidation = (() => {
    if (!editedStartDate || !editedStartTime || !editedEndDate || !editedEndTime) {
      return { valid: false, message: 'Please fill in all date and time fields' };
    }
    try {
      const start = new Date(`${editedStartDate}T${editedStartTime}`);
      const end = new Date(`${editedEndDate}T${editedEndTime}`);
      const diffMinutes = (end.getTime() - start.getTime()) / (1000 * 60);

      if (diffMinutes < 0) {
        return { valid: false, message: 'Complete By cannot be before the Scheduled time' };
      }
      if (diffMinutes < 30) {
        return { valid: false, message: `Complete By must be at least 30 minutes after Scheduled time (currently ${Math.round(diffMinutes)} min)` };
      }
      return { valid: true, message: '' };
    } catch {
      return { valid: false, message: 'Invalid date or time' };
    }
  })();

  const handleSaveSchedule = async () => {
    if (!onUpdateSchedule) return;
    if (!scheduleValidation.valid) return;
    
    setIsSaving(true);
    try {
      await onUpdateSchedule({
        startDate: editedStartDate,
        startTime: editedStartTime,
        endDate: editedEndDate,
        endTime: editedEndTime
      });
      setIsEditingSchedule(false);
    } catch (error) {
      console.error('Error saving schedule:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingSchedule(false);
  };
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Status management
  const statuses = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' }
  ];

  const currentStatusIndex = statuses.findIndex(s => s.value === booking.status);
  const canStatusGoUp = currentStatusIndex > 0;
  const canStatusGoDown = currentStatusIndex < statuses.length - 1;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'created':
        return 'text-gray-500';
      case 'scheduled':
        return 'text-blue-600';
      case 'in_progress':
        return 'text-yellow-600';
      case 'completed':
        return 'text-green-600';
      default:
        return 'text-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-3 w-3" />;
      case 'in_progress':
        return <Clock className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const handleStatusUp = () => {
    if (canStatusGoUp && onStatusChange) {
      onStatusChange(statuses[currentStatusIndex - 1].value);
    }
  };

  const handleStatusDown = () => {
    if (canStatusGoDown && onStatusChange) {
      onStatusChange(statuses[currentStatusIndex + 1].value);
    }
  };
  
  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Format relative time
  const getRelativeTime = (date?: string, time?: string) => {
    if (!date) return null;
    
    try {
      const dateTime = parseISO(`${date}T${time || '00:00'}`);
      const now = currentTime;
      
      if (isBefore(dateTime, now)) {
        return `${formatDistanceToNow(dateTime)} ago`;
      } else {
        return `in ${formatDistanceToNow(dateTime)}`;
      }
    } catch {
      return null;
    }
  };

  // Determine timeline points
  const getTimelinePoints = (): TimePoint[] => {
    const points: TimePoint[] = [];
    
    if (!booking.mobile_booking) {
      points.push({
        type: 'drop-off',
        date: booking.drop_off_date,
        time: booking.drop_off_time,
        label: 'Drop-off',
        isComplete: booking.status !== 'created' && booking.status !== 'scheduled'
      });
    }
    
    points.push({
      type: 'start',
      date: booking.start_date,
      time: booking.start_time,
      label: booking.mobile_booking ? 'Scheduled' : 'Start',
      isComplete: booking.status === 'in_progress' || booking.status === 'completed',
      isCurrent: booking.status === 'in_progress'
    });
    
    points.push({
      type: 'end',
      date: booking.end_date,
      time: booking.end_time,
      label: booking.mobile_booking ? 'Complete By' : 'Pick-up',
      isComplete: booking.status === 'completed'
    });
    
    return points;
  };

  const timelinePoints = getTimelinePoints();

  // If editing schedule, show inline editor
  if (isEditingSchedule && booking.mobile_booking) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900">Edit Schedule</h3>
        </div>

        {/* Start Date & Time */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Scheduled
          </label>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              value={editedStartDate}
              onChange={(e) => setEditedStartDate(e.target.value)}
              disabled={isSaving}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-50"
            />
            <input
              type="time"
              value={editedStartTime}
              onChange={(e) => setEditedStartTime(e.target.value)}
              disabled={isSaving}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-50"
            />
          </div>
        </div>

        {/* End Date & Time */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Completed By
          </label>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              value={editedEndDate}
              onChange={(e) => handleEndDateChange(e.target.value)}
              disabled={isSaving}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-50"
            />
            <input
              type="time"
              value={editedEndTime}
              onChange={(e) => handleEndTimeChange(e.target.value)}
              disabled={isSaving}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-50"
            />
          </div>
        </div>

        {/* Validation Warning */}
        {isEditingSchedule && !scheduleValidation.valid && editedStartDate && editedStartTime && editedEndDate && editedEndTime && (
          <div className="flex items-center gap-2 p-2.5 bg-red-50 border border-red-200 rounded-lg">
            <svg className="h-4 w-4 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
            </svg>
            <span className="text-xs text-red-700 font-medium">{scheduleValidation.message}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <button
            onClick={handleSaveSchedule}
            disabled={isSaving || !scheduleValidation.valid}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:scale-[0.98] transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Save
              </>
            )}
          </button>
          <button
            onClick={handleCancelEdit}
            disabled={isSaving}
            className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:scale-[0.98] transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* iOS-style Timeline Cards - Ultra Compact */}
      <div className={`grid ${timelinePoints.length === 2 ? 'grid-cols-2' : 'grid-cols-3'} gap-2`}>
        {timelinePoints.map((point, index) => {
          const relativeTime = getRelativeTime(point.date, point.time);
          const isActive = point.isCurrent || (booking.status === 'in_progress' && point.type === 'start');
          const isClickable = (canEdit && onEdit && !isEditing) || (booking.mobile_booking && onUpdateSchedule);
          
          return (
            <div
              key={point.type}
              onClick={() => {
                if (booking.mobile_booking && onUpdateSchedule) {
                  // Open inline editor for mobile bookings
                  setIsEditingSchedule(true);
                } else if (canEdit && onEdit && !isEditing) {
                  onEdit();
                }
              }}
              className={`
                bg-white rounded-[10px] border border-gray-200/60 p-1.5
                ${isClickable ? 'cursor-pointer active:scale-[0.98] touch-manipulation' : ''}
                transition-all duration-150
                ${point.isComplete ? 'shadow-sm' : 'shadow-[0_1px_2px_rgba(0,0,0,0.05)]'}
              `}
              style={{
                WebkitTapHighlightColor: 'transparent',
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
              }}
            >
              {/* Label */}
              <div className="flex items-center justify-between mb-0.5">
                <span className={`
                  text-[10px] font-semibold uppercase tracking-[0.3px]
                  ${point.isComplete 
                    ? 'text-green-600' 
                    : isActive
                      ? 'text-blue-600'
                      : 'text-gray-500'
                  }
                `}>
                  {point.label}
                </span>
                {point.isComplete && (
                  <CheckCircle2 className="h-2.5 w-2.5 text-green-600 flex-shrink-0" />
                )}
                {isActive && !point.isComplete && (
                  <Clock className="h-2.5 w-2.5 text-blue-600 flex-shrink-0" />
                )}
              </div>

              {/* Date & Time */}
              {point.date && point.time ? (
                <div className="text-[12px] font-medium text-gray-900 leading-tight whitespace-nowrap">
                  {format(parseISO(`${point.date}T${point.time}`), isMobile ? 'MMM d' : 'MMM d')} • {format(parseISO(`2000-01-01T${point.time}`), 'h:mm a')}
                </div>
              ) : (
                <div className="text-[11px] text-gray-400 font-medium">Not scheduled</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}